import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import {
  Alert,
  Box,
  ConfirmInput,
  Spinner,
  useAsync,
  useAsyncCallback,
} from '@wix/cli-ui-kit';
import { writeJson } from '@wix/cli-fs';
import { getMultilingualMetadataPath } from '@wix/cli-astro-definitions';
import { useMultilingualClient } from '@wix/multilingual-client';
import { useProjectModel } from '../../model.js';
import { useWixCliAstroBi } from '../../bi/index.js';
import type { TranslationsData } from './read-translations-file.js';

interface TranslationPushProps {
  translations: TranslationsData;
  onComplete?: () => void;
  skipConfirmation?: boolean;
}

const CONFIRM_PUSH_QUESTION =
  'Push translations? Unused keys will be deleted, new ones added. This cannot be undone.';

export const TranslationPush: FC<TranslationPushProps> = ({
  translations,
  onComplete,
  skipConfirmation,
}) => {
  const { model } = useProjectModel();
  const multilingualClient = useMultilingualClient();
  const bi = useWixCliAstroBi();

  const { status, execute } = useAsyncCallback(async () => {
    const primaryLanguageCode = await multilingualClient.getPrimaryLanguage();

    const metadataPath = getMultilingualMetadataPath(model.projectFolder);
    await writeJson(metadataPath, { primaryLanguageCode }, { spaces: 2 });

    await multilingualClient.pushTranslations(translations);
    return { primaryLanguageCode };
  });

  useAsync(async () => {
    if (!skipConfirmation) {
      return;
    }
    execute()
      .then(() => onComplete?.())
      .catch(() => {});
  }, []);

  return (
    <>
      {!skipConfirmation && (
        <Box marginBottom={1}>
          <ConfirmInput
            label={CONFIRM_PUSH_QUESTION}
            initialValue={true}
            onSubmit={(confirmed) => {
              bi.cliFlowStepAnswered({
                question: CONFIRM_PUSH_QUESTION,
                questionKey: 'translation_push_command.confirm_push',
                answer: String(confirmed),
              });

              if (confirmed) {
                execute()
                  .then(() => onComplete?.())
                  .catch(() => {});
              } else {
                onComplete?.();
              }
            }}
          />
        </Box>
      )}
      {match(status, {
        NotRequested: () => null,
        Loading: () => <Spinner text="Pushing translation file..." />,
        Success: ({ result: { primaryLanguageCode } }) => (
          <Alert type="success">
            Translation keys of "{primaryLanguageCode}" were successfully pushed
          </Alert>
        ),
        Error: () => null,
      })}
    </>
  );
};
